/**
 * CountInput — the ONE count box of cafeteria mode (PROD-1278 feedback,
 * 2026-09-11): on-hand and for-sale counts, served / reimbursable / leftover
 * tallies, delivery and write-in quantities. A count is a non-negative
 * integer typed by a worker on a stand-mounted iPad, so:
 *
 *   - `type="text"` + `inputMode="numeric"` + `pattern="[0-9]*"`, never
 *     `type="number"`: the number type lets `e`, `-` and `.` through, moves
 *     its value under a scroll wheel, and on the iPad still raised the full
 *     numbers-and-punctuation keyboard with `inputMode="numeric"` set
 *     (preview 2ybodavp1w3c, 2026-09-11). Text arriving any other way (a
 *     paste, a scanner) is reduced to its digits before the parent sees it.
 *   - On a TOUCH device (`(pointer: coarse)`) the box asks iOS for NO
 *     keyboard (`inputMode="none"`) and, while it has the focus, shows the
 *     `NumericKeypad` in a popover above itself — the digits-only pad the ask
 *     pointed at (Apple Books' page pad), which iPadOS does not offer to web
 *     content. The pad follows the focus and nothing else: it opens when the
 *     box is focused, closes when the box is left (the pad's own Done key
 *     blurs the box for workers who would not think to tap outside), and its
 *     keys never take the focus, so the box keeps its caret and a hardware
 *     keyboard or a scanner still types straight into it. A mouse / trackpad
 *     device gets no pad; the box is an ordinary text box there.
 *
 * `NumericKeypadPopover` is that pad-over-a-box behaviour on its own, so the
 * temperature box (`components/tasks/temp-checkpoint-input.tsx`) shows the
 * same pad, in its decimal layout: every numeric box on a tablet uses the
 * app's keypad (Abner, 2026-09-15).
 *
 * Controlled by TEXT: `value` is the box's text (a number is stringified),
 * `onValueChange` receives the digits-only text. `''` means "cleared", which
 * every call site already maps its own way (blank = trust the delivery
 * numbers on the inventory surfaces; `Number('') === 0` on the tallies).
 *
 * The popover is explicitly NON-modal: a modal Radix popover traps the focus
 * inside its content, and the whole point is that the focus stays on the box.
 */

import { cn } from '@ordo/ui/cn'
import { Input } from '@ordo/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@ordo/ui/popover'
import { type ComponentProps, type ReactElement, useRef, useState } from 'react'

import { VirtualKeyButton } from '@/components/cafeteria/serve/virtual-keyboard'
import { useCoarsePointer } from '@/hooks/use-coarse-pointer'
import {
  applyNumericKeypadKey,
  NUMERIC_KEYPAD_DECIMAL_ROWS,
  NUMERIC_KEYPAD_ROWS,
  type NumericKeypadKey,
  sanitizeCountText,
} from '@/lib/cafeteria/numeric-keypad'
import { virtualKeyId } from '@/lib/cafeteria/virtual-keyboard'

type InputProps = ComponentProps<typeof Input>

export interface CountInputProps extends Omit<
  InputProps,
  'type' | 'inputMode' | 'pattern' | 'autoComplete' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step'
> {
  /** The box's text; a number reads as its decimal digits. */
  value: string | number
  /** The digits-only text after a keystroke, a press or a paste; `''` when the box was emptied. */
  onValueChange: (next: string) => void
}

export function CountInput({ value, onValueChange, onFocus, onBlur, disabled, className, ...props }: CountInputProps) {
  const coarsePointer = useCoarsePointer()
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const text = typeof value === 'number' ? String(value) : value
  // A disabled box takes no focus and shows no pad; it also asks for the
  // numeric keyboard again, so a box re-enabled under a finger reads right.
  const keypad = coarsePointer && !disabled
  const open = keypad && focused

  return (
    <NumericKeypadPopover
      open={open}
      onKey={(key) => {
        if (key.kind === 'confirm') {
          inputRef.current?.blur()
          return
        }
        onValueChange(applyNumericKeypadKey(text, key))
      }}>
      <Input
        ref={inputRef}
        type="text"
        inputMode={keypad ? 'none' : 'numeric'}
        pattern="[0-9]*"
        autoComplete="off"
        value={text}
        disabled={disabled}
        className={cn('tabular-nums', className)}
        onChange={(event) => onValueChange(sanitizeCountText(event.target.value))}
        onFocus={(event) => {
          setFocused(true)
          onFocus?.(event)
        }}
        onBlur={(event) => {
          setFocused(false)
          onBlur?.(event)
        }}
        {...props}
      />
    </NumericKeypadPopover>
  )
}

export interface NumericKeypadPopoverProps {
  /** Whether the pad shows: the host's "touch device AND the box has the focus". */
  open: boolean
  /** The temperature layout (`. 0 ⌫` + Done) instead of the count layout (`✓ 0 ⌫`). */
  decimal?: boolean
  /** One press. Done arrives here too: the host blurs its box. */
  onKey: (key: NumericKeypadKey) => void
  /** The box the pad floats above — one element that takes a ref. */
  children: ReactElement
}

/**
 * The pad in a popover above a box. Explicitly NON-modal: a modal Radix
 * popover traps the focus inside its content, and the whole point is that the
 * focus stays on the box (the pad neither takes it on open nor hands it
 * anywhere on close).
 */
export function NumericKeypadPopover({ open, decimal = false, onKey, children }: NumericKeypadPopoverProps) {
  return (
    <Popover open={open} modal={false}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      {open ? (
        <PopoverContent
          side="top"
          align="center"
          sideOffset={8}
          aria-label="Number keypad"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="w-auto p-2">
          <NumericKeypad decimal={decimal} onKey={onKey} />
        </PopoverContent>
      ) : null}
    </Popover>
  )
}

export interface NumericKeypadProps {
  /** One press; the box applies it to its text (Done: the box blurs). */
  onKey: (key: NumericKeypadKey) => void
  /** The temperature layout: `.` bottom-left, Done as its own full-width row. */
  decimal?: boolean
  /** Keys share the pad's width instead of a fixed 64px: the phone's docked pad (PROD-1511). */
  stretch?: boolean
  className?: string
}

/**
 * The pad itself: the rows from `numeric-keypad.ts` (`✓ 0 ⌫` closes the count
 * pad; the decimal pad puts `.` there and Done on a row of its own, filling
 * the width), every key a `VirtualKeyButton` (so it never takes the focus).
 * Rendered on its own so a test can drive it without a popover.
 */
export function NumericKeypad({ onKey, decimal = false, stretch = false, className }: NumericKeypadProps) {
  const rows = decimal ? NUMERIC_KEYPAD_DECIMAL_ROWS : NUMERIC_KEYPAD_ROWS
  return (
    <div
      role="group"
      aria-label="Number keypad"
      data-testid="numeric-keypad"
      data-decimal={decimal || undefined}
      className={cn('flex flex-col gap-2', className)}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} data-row={rowIndex} className="flex gap-2">
          {row.map((cell, cellIndex) =>
            cell === null ? (
              <span
                key={`empty-${cellIndex}`}
                aria-hidden
                data-empty
                className={cn('h-14', stretch ? 'flex-1' : 'w-16')}
              />
            ) : (
              <VirtualKeyButton
                key={virtualKeyId(cell)}
                virtualKey={cell}
                className={cn('h-14 rounded-xl', row.length === 1 || stretch ? 'min-w-0 flex-1' : 'w-16')}
                onKey={onKey}
              />
            ),
          )}
        </div>
      ))}
    </div>
  )
}
